import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronRight, ChevronLeft, User, Baby } from 'lucide-react';

const OnboardingPage = ({ onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [parentName, setParentName] = useState('');
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge] = useState(null);

  const ageOptions = [3, 4, 5, 6];

  const handleNext = () => {
    if (step === 1 && parentName.trim()) {
      setStep(2);
    } else if (step === 2 && childName.trim()) {
      setStep(3);
    } else if (step === 3 && childAge) {
      onComplete(parentName.trim(), childName.trim(), childAge);
      navigate('/home');
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate('/');
    }
  };

  const canProceed = () => {
    if (step === 1) return parentName.trim().length > 0;
    if (step === 2) return childName.trim().length > 0;
    if (step === 3) return childAge !== null;
    return false;
  };

  return (
    <div className="app-container min-h-screen bg-background flex flex-col px-6 py-8 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="w-10 h-10 rounded-full"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>

        {/* Progress dots */}
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <motion.div
              key={i}
              className={`progress-dot ${i === step ? 'progress-dot-active' : ''} ${i < step ? 'progress-dot-complete' : ''}`}
              animate={{ scale: i === step ? 1.2 : 1 }}
            />
          ))}
        </div>

        <div className="w-10" />
      </div>

      {/* Step content */}
      <div className="flex-1 flex flex-col">
        <AnimatePresence mode="wait">
          {/* Step 1: Parent Name */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex-1 flex flex-col"
            >
              <div className="mb-8">
                <div className="w-16 h-16 rounded-2xl bg-primary-light flex items-center justify-center mb-6">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-display font-bold text-foreground mb-2">
                  Привет!
                </h1>
                <p className="text-muted-foreground">
                  Как вас зовут?
                </p>
              </div>

              <div className="space-y-3">
                <Label htmlFor="parentName" className="text-sm font-medium">
                  Ваше имя
                </Label>
                <Input
                  id="parentName"
                  type="text"
                  placeholder="Например, Мама или Папа"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  className="h-14 text-lg rounded-xl"
                  autoFocus
                />
              </div>
            </motion.div>
          )}

          {/* Step 2: Child Name */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex-1 flex flex-col"
            >
              <div className="mb-8">
                <div className="w-16 h-16 rounded-2xl bg-secondary-light flex items-center justify-center mb-6">
                  <Baby className="w-8 h-8 text-secondary-foreground" />
                </div>
                <h1 className="text-2xl font-display font-bold text-foreground mb-2">
                  Отлично, {parentName}!
                </h1>
                <p className="text-muted-foreground">
                  Как зовут вашего ребёнка?
                </p>
              </div>

              <div className="space-y-3">
                <Label htmlFor="childName" className="text-sm font-medium">
                  Имя ребёнка
                </Label>
                <Input
                  id="childName"
                  type="text"
                  placeholder="Например, Аня"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  className="h-14 text-lg rounded-xl"
                  autoFocus
                />
              </div>
            </motion.div>
          )}

          {/* Step 3: Child Age */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex-1 flex flex-col"
            >
              <div className="mb-8">
                <h1 className="text-2xl font-display font-bold text-foreground mb-2">
                  Сколько {childName} лет?
                </h1>
                <p className="text-muted-foreground">
                  Это поможет настроить занятия
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {ageOptions.map((age) => (
                  <motion.button
                    key={age}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setChildAge(age)}
                    className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                      childAge === age
                        ? 'border-primary bg-primary-light'
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <span className="text-3xl font-display font-bold block mb-1">
                      {age}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {age === 3 ? 'года' : age === 4 ? 'года' : age === 5 ? 'лет' : 'лет'}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Continue button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button
          onClick={handleNext}
          disabled={!canProceed()}
          className="btn-child-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {step === 3 ? 'Начать занятия' : 'Дальше'}
          <ChevronRight className="w-5 h-5" />
        </Button>
      </motion.div>
    </div>
  );
};

export default OnboardingPage;
